import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Download, Eye, EyeOff, RotateCcw } from 'lucide-react';

const RoomSimulatorV2 = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  // Room shape selection
  const [roomShape, setRoomShape] = useState('rectangle');
  const [step, setStep] = useState(1); // 1: Shape, 2: Dimensions, 3: View
  
  // Dimensions (in meters)
  const [dimensions, setDimensions] = useState({
    length: 5,
    width: 4,
    height: 2.5,
    // For L-shape
    length2: 3,
    width2: 2
  });

  const [scale] = useState(40); // pixels per meter
  const [show3D, setShow3D] = useState(false);

  // Room shapes available
  const shapes = [
    { id: 'rectangle', name: 'Rectangle', icon: '▭' },
    { id: 'square', name: 'Carré', icon: '□' },
    { id: 'l-shape', name: 'En L', icon: '⌐' },
  ];

  const calculateArea = () => {
    if (roomShape === 'rectangle' || roomShape === 'square') {
      return dimensions.length * dimensions.width;
    } else if (roomShape === 'l-shape') {
      // L-shape = big rectangle - small missing rectangle
      const totalArea = dimensions.length * dimensions.width;
      const missingArea = (dimensions.length - dimensions.length2) * (dimensions.width - dimensions.width2);
      return totalArea - missingArea;
    }
    return 0;
  };

  const calculatePerimeter = () => {
    if (roomShape === 'rectangle' || roomShape === 'square') {
      return 2 * (dimensions.length + dimensions.width);
    } else if (roomShape === 'l-shape') {
      return 2 * (dimensions.length + dimensions.width + dimensions.length2 + dimensions.width2);
    }
    return 0;
  };

  // Draw on 2D canvas
  useEffect(() => {
    if (step < 2) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw grid
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    for (let x = 0; x <= width; x += scale) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      if (x % (scale * 5) === 0) {
        ctx.fillStyle = '#999';
        ctx.font = '10px Inter';
        ctx.fillText(`${x / scale}m`, x + 2, 12);
      }
    }
    for (let y = 0; y <= height; y += scale) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
      if (y % (scale * 5) === 0) {
        ctx.fillStyle = '#999';
        ctx.font = '10px Inter';
        ctx.fillText(`${y / scale}m`, 2, y + 12);
      }
    }

    // Center offset
    const offsetX = width / 2;
    const offsetY = height / 2;

    // Draw room based on shape
    ctx.strokeStyle = '#0B1F3C';
    ctx.lineWidth = 4;
    ctx.fillStyle = 'rgba(201, 169, 97, 0.15)';

    if (roomShape === 'rectangle' || roomShape === 'square') {
      const rectWidth = dimensions.length * scale;
      const rectHeight = dimensions.width * scale;
      const x = offsetX - rectWidth / 2;
      const y = offsetY - rectHeight / 2;

      ctx.fillRect(x, y, rectWidth, rectHeight);
      ctx.strokeRect(x, y, rectWidth, rectHeight);

      // Draw dimensions
      ctx.fillStyle = '#0B1F3C';
      ctx.font = 'bold 14px Inter';
      // Length (horizontal)
      ctx.fillText(`${dimensions.length}m`, offsetX - 20, y - 10);
      // Width (vertical)
      ctx.save();
      ctx.translate(x - 15, offsetY);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(`${dimensions.width}m`, -20, 0);
      ctx.restore();

    } else if (roomShape === 'l-shape') {
      const len1 = dimensions.length * scale;
      const wid1 = dimensions.width * scale;
      const len2 = dimensions.length2 * scale;
      const wid2 = dimensions.width2 * scale;

      const x = offsetX - len1 / 2;
      const y = offsetY - wid1 / 2;

      ctx.beginPath();
      // L-shape path
      ctx.moveTo(x, y);
      ctx.lineTo(x + len1, y);
      ctx.lineTo(x + len1, y + wid2);
      ctx.lineTo(x + len2, y + wid2);
      ctx.lineTo(x + len2, y + wid1);
      ctx.lineTo(x, y + wid1);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Draw dimensions
      ctx.fillStyle = '#0B1F3C';
      ctx.font = 'bold 12px Inter';
      ctx.fillText(`${dimensions.length}m`, x + len1 / 2 - 15, y - 10);
      ctx.fillText(`${dimensions.width}m`, x + len1 + 10, y + wid2 / 2);
    }

    // Draw legend
    ctx.fillStyle = '#666';
    ctx.font = '12px Inter';
    ctx.fillText('Vue du dessus - Plan 2D', 10, height - 10);

  }, [roomShape, dimensions, step, scale]);

  const handleShapeSelect = (shape) => {
    setRoomShape(shape);
    if (shape === 'square') {
      setDimensions(prev => ({ ...prev, width: prev.length }));
    }
    setStep(2);
  };

  const handleDimensionChange = (key, value) => {
    const numValue = parseFloat(value) || 0;
    setDimensions(prev => {
      const newDims = { ...prev, [key]: numValue };
      if (roomShape === 'square' && (key === 'length' || key === 'width')) {
        newDims.length = numValue;
        newDims.width = numValue;
      }
      return newDims;
    });
  };

  const area = calculateArea();
  const perimeter = calculatePerimeter();
  const volume = area * dimensions.height;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="h-20"></div>
      
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={20} />
          Retour à l'accueil
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0B1F3C] mb-4">
            Simulateur de Pièce 2D/3D
          </h1>
          <p className="text-gray-600 text-lg">
            Créez le plan de votre pièce en 3 étapes simples et obtenez instantanément les dimensions.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8 flex items-center justify-center gap-4">
          <div className={`flex items-center ${step >= 1 ? 'text-[#c9a961]' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-[#c9a961] text-white' : 'bg-gray-200'}`}>
              1
            </div>
            <span className="ml-2 font-medium hidden sm:inline">Forme</span>
          </div>
          <div className="w-12 h-1 bg-gray-300"></div>
          <div className={`flex items-center ${step >= 2 ? 'text-[#c9a961]' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-[#c9a961] text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="ml-2 font-medium hidden sm:inline">Dimensions</span>
          </div>
          <div className="w-12 h-1 bg-gray-300"></div>
          <div className={`flex items-center ${step >= 3 ? 'text-[#c9a961]' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 3 ? 'bg-[#c9a961] text-white' : 'bg-gray-200'}`}>
              3
            </div>
            <span className="ml-2 font-medium hidden sm:inline">Visualisation</span>
          </div>
        </div>

        {/* Step 1: Shape Selection */}
        {step === 1 && (
          <Card className="max-w-3xl mx-auto border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-[#0B1F3C] to-[#1a3a52] text-white">
              <CardTitle>Étape 1 : Choisissez la forme de votre pièce</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {shapes.map((shape) => (
                  <button
                    key={shape.id}
                    onClick={() => handleShapeSelect(shape.id)}
                    className="p-8 border-2 border-gray-300 rounded-xl hover:border-[#c9a961] hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">
                      {shape.icon}
                    </div>
                    <h3 className="text-xl font-bold text-[#0B1F3C] group-hover:text-[#c9a961] transition-colors">
                      {shape.name}
                    </h3>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2 & 3: Dimensions and View */}
        {step >= 2 && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Canvas Area */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-[#0B1F3C] to-[#1a3a52] text-white">
                  <CardTitle className="flex items-center justify-between">
                    <span>Plan de votre pièce</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="text-white border-white hover:bg-white hover:text-[#0B1F3C]"
                    >
                      <RotateCcw size={16} className="mr-2" />
                      Changer
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <canvas
                    ref={canvasRef}
                    width={800}
                    height={600}
                    className="border-2 border-gray-300 rounded-lg w-full"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Dimensions Panel */}
            <div className="space-y-6">
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-[#c9a961] text-white">
                  <CardTitle>Dimensions (en mètres)</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {(roomShape === 'rectangle' || roomShape === 'square') && (
                    <>
                      <div>
                        <Label className="text-sm text-gray-600">
                          {roomShape === 'square' ? 'Côté (m)' : 'Longueur (m)'}
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={dimensions.length}
                          onChange={(e) => handleDimensionChange('length', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      {roomShape === 'rectangle' && (
                        <div>
                          <Label className="text-sm text-gray-600">Largeur (m)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0.1"
                            value={dimensions.width}
                            onChange={(e) => handleDimensionChange('width', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {roomShape === 'l-shape' && (
                    <>
                      <div>
                        <Label className="text-sm text-gray-600">Longueur totale (m)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={dimensions.length}
                          onChange={(e) => handleDimensionChange('length', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Largeur totale (m)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={dimensions.width}
                          onChange={(e) => handleDimensionChange('width', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Longueur partie 2 (m)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={dimensions.length2}
                          onChange={(e) => handleDimensionChange('length2', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Largeur partie 2 (m)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={dimensions.width2}
                          onChange={(e) => handleDimensionChange('width2', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <Label className="text-sm text-gray-600">Hauteur sous plafond (m)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={dimensions.height}
                      onChange={(e) => handleDimensionChange('height', e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-600 font-medium">Surface au sol :</span>
                      <span className="text-2xl font-bold text-[#0B1F3C]">
                        {area.toFixed(2)} m²
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-600">Périmètre :</span>
                      <span className="text-lg font-semibold text-[#0B1F3C]">
                        {perimeter.toFixed(2)} m
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Volume :</span>
                      <span className="text-lg font-semibold text-[#0B1F3C]">
                        {volume.toFixed(2)} m³
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <Card className="border-0 shadow-xl bg-gradient-to-br from-[#0B1F3C] to-[#1a3a52] text-white">
                <CardContent className="p-6">
                  <h3 className="font-bold mb-4">Actions</h3>
                  <div className="space-y-3">
                    <Button
                      onClick={() => alert('Export PDF à venir !')}
                      className="w-full bg-[#c9a961] hover:bg-[#b89951]"
                    >
                      <Download className="mr-2" size={18} />
                      Télécharger le plan (PDF)
                    </Button>
                    <Button
                      onClick={() => navigate('/#contact')}
                      variant="outline"
                      className="w-full border-white text-white hover:bg-white hover:text-[#0B1F3C]"
                    >
                      Demander un devis
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomSimulatorV2;
